import { FormGroup } from '@angular/forms';

import { getFormValidationErrors } from '../../../validators';

/**
 * Represents an individual error with a message and optional metadata.
 * Used within the Result class to encapsulate error details.
 */
export class GuardFinderError {
    /**
     * Creates an Error instance.
     * @param message - The error message (required).
     * @param code - An optional error code (e.g., 'VALIDATION_FAILED').
     * @param metadata - Optional additional data (e.g., field name for validation errors).
     */
    constructor(
        public readonly message: string,
        public readonly code?: string,
        public readonly metadata?: Record<string, unknown>
    ) {}

    static fromException(error: Error): GuardFinderError {
        return new GuardFinderError(error.message);
    }

    static fromString(message: string): GuardFinderError {
        return new GuardFinderError(message);
    }

    /**
     * Creates an array of GuardFinderError instances from the validation errors of a given FormGroup.
     *
     * @param {FormGroup} formGroup - The FormGroup instance from which validation errors are extracted.
     * @return {GuardFinderError[]} An array of GuardFinderError objects representing the validation errors found in the FormGroup.
     */
    static createFromFormGroupErrors(formGroup: FormGroup): GuardFinderError[] {
        const errors: GuardFinderError[] = [];
        const validationErrors = getFormValidationErrors(formGroup);

        validationErrors.forEach(error => {
            errors.push(
                new GuardFinderError(`${error.control}: ${error.error}`, 'FORM_VALIDATION_ERROR', {
                    control: error.control,
                    error: error.error,
                    value: error.value
                })
            );
        });

        return errors;
    }

    /**
     * Returns a string representation of the error.
     */
    toString(): string {
        return this.message;
    }
}
